/* eslint-disable max-lines */
import {makeAutoObservable, observable, runInAction} from 'mobx'
import React from 'react'
import {logger, UIUtil} from 'yougile-util'
import {Debouncer} from '@common/debouncer'
import {DragClick} from '@common/drag/drag-click'
import {DragRectangle} from '@common/drag/drag-common'
import {UtilUi} from '@common/utils-ui'
import {
  DnDDataPayload,
  DnDDataType,
  DraggableProps,
  DroppableItem
} from '@components/draggable'
import {primaryButtonPressed} from '@components/draggable/utils'
import {createUseStore} from '@containers/hooks'
import {insideRect} from '@containers/scroll-utils'
import {
  KeyboardHandlers,
  KeyboardHandlersType
} from '@extensions/keyboard-handlers'
import {cloneOriginElement, isOutsideContainer} from './utils'

export type ScrollDirection = 'x' | 'y' | 'both'

interface ScrollContainer {
  container: HTMLElement
  direction: ScrollDirection
  allowOutside: boolean
  thresholdX: number
  thresholdY: number
}

interface State {
  droppables: DroppableItem[]
  currentDroppable?: DroppableItem
  draggable?: HTMLElement
  draggableRect?: DOMRect
  draggableProps?: DraggableOnMouseDownProps
  ghost?: HTMLElement
  mouseDownXY?: XY
  mouseXY?: XY
  dragStarted: boolean
  placeholder?: HTMLElement
  isPlaceholderAfterDroppable?: boolean
  scrollInterval?: number
  lastContainer?: HTMLElement
  droppablesSortRequired?: boolean
  keydownHandler?: KeyboardHandlersType
  canBeHiddenOnDrag?: boolean
}

export class DnDStore {
  state: State

  constructor() {
    const escape = (): boolean | void => {
      if (this.state.dragStarted) {
        this.dragStopCleanup()
        return true
      }
    }

    this.state = {
      droppables: [],
      dragStarted: false,
      keydownHandler: {escape}
    }

    makeAutoObservable<this>(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
    this.init()
  }

  getStarted<T extends DnDDataType>(type: T): DnDDataPayload<T> | undefined {
    return this.state.dragStarted &&
      this.state.draggableProps?.data.type === type
      ? (this.state.draggableProps.data.payload as DnDDataPayload<T>)
      : undefined
  }

  addDroppable(droppable: DroppableItem): void {
    this.state.droppables.push(droppable)
    this.state.droppablesSortRequired = true
  }

  removeDroppable(id: string): void {
    const idx = this.state.droppables.findIndex(d => d.id === id)
    if (idx > -1) {
      this.state.droppables.splice(idx, 1)
    }
  }

  private init(): void {
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
  }

  dispose(): void {
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.state.mouseDownXY) {
      return
    }
    if (!primaryButtonPressed(e)) {
      this.onMouseUp(e)
      return
    }
    if (this.state.dragStarted) {
      this.updateGhostPosition(e)
      this.processDroppablesNotOften(e)
      return
    }

    if (
      Math.abs(e.pageX - this.state.mouseDownXY.x) > DELTA_TO_DRAG_START ||
      Math.abs(e.pageY - this.state.mouseDownXY.y) > DELTA_TO_DRAG_START
    ) {
      this.dragStart(e)
    }
  }

  private updateGhostPosition(e: MouseEvent): void {
    if (
      !this.state.mouseDownXY ||
      !this.state.ghost ||
      !this.state.draggableRect
    ) {
      return
    }
    const isInDropArea =
      !this.state.draggableProps?.dropArea ||
      isInside(this.state.draggableProps.dropArea, e)
    const dx =
      e.pageX -
      (isInDropArea
        ? this.state.mouseDownXY.x
        : this.state.draggableRect.left - 10)
    const dy =
      e.pageY -
      (isInDropArea
        ? this.state.mouseDownXY.y
        : this.state.draggableRect.top - 10)

    const left = `${this.state.draggableRect.left}px`
    const top = `${this.state.draggableRect.top}px`

    if (left !== this.state.ghost.style.left) {
      this.state.ghost.style.left = left
    }

    if (top !== this.state.ghost.style.top) {
      this.state.ghost.style.top = top
    }
    this.state.ghost.style.transform = `translate(${dx}px, ${dy}px)`

    this.state.mouseXY = {x: e.pageX, y: e.pageY}
  }

  private processDroppablesNotOften(e: MouseEvent): void {
    droppablesDebounce.runButNotOften(() => this.processDroppables(e))
  }

  private processDroppables(e: MouseEvent): void {
    const newPosition = this.placeholderMode
      ? this.getNewPositionForManagedPlaceholderMode(e)
      : this.getNewPosition(e)

    if (
      this.placeholderMode &&
      newPosition?.droppable?.showPlaceholder &&
      this.state.canBeHiddenOnDrag
    ) {
      this.state.draggable?.classList.add('hidden')
    }

    if (!newPosition || !this.state.draggableProps) {
      return
    }

    this.placeholderMode && this.updatePlaceholder(newPosition)

    const {data} = this.state.draggableProps
    const newDroppable = newPosition.droppable
    const oldDroppable = this.state.currentDroppable
    if (oldDroppable?.id !== newDroppable?.id) {
      oldDroppable?.onDragLeave?.(data, e, oldDroppable?.element)
      newDroppable?.onDragEnter?.(data, e, newDroppable?.element)
      this.state.currentDroppable = newDroppable
    }
    newDroppable?.onDragOver?.(data, e, newDroppable?.element)
  }

  private getNewPositionForManagedPlaceholderMode(
    e: MouseEvent
  ): PlaceholderPosition | undefined {
    if (!this.state.ghost || !this.state.draggableProps) {
      return undefined
    }
    const {draggableProps} = this.state
    const {left, right, top, bottom} = this.state.ghost.getBoundingClientRect()
    const placeholderRect = this.state.placeholder?.getBoundingClientRect()
    const middleY = draggableProps.trigger === 'middleY'
    const allowOutside = !!draggableProps.allowPlaceholderOutsideMovement
    const placeholderBefore =
      placeholderRect &&
      (middleY ? placeholderRect.top < top : placeholderRect.left < left)
    const isNewContainer =
      !this.state.placeholder ||
      isOutsideContainer({droppable: this.state.currentDroppable, e})
    let lastDroppableInContainer: DroppableItem | undefined
    const first = middleY ? top : left
    const second = middleY ? bottom : right
    const ghostSide = isNewContainer || !placeholderBefore ? first : second

    this.sortDroppablesIfRequired()
    const droppable = this.state.droppables.find(droppable => {
      if (
        droppable.accepts &&
        !droppable.accepts.includes(draggableProps.data.type)
      ) {
        return false
      }

      if (!allowOutside && isOutsideContainer({droppable, e})) {
        return false
      }

      lastDroppableInContainer = droppable
      const rect = droppable.element.getBoundingClientRect()
      if (!rect.width || !rect.height) {
        return false
      }

      const mid = middleY
        ? (rect.top + rect.bottom) / 2
        : (rect.left + rect.right) / 2
      return ghostSide < mid
    })

    return lastDroppableInContainer
      ? {
          droppable: droppable ?? lastDroppableInContainer,
          isAfter: !droppable
        }
      : undefined
  }

  private getNewPosition(e: MouseEvent): PlaceholderPosition | undefined {
    if (!this.state.ghost || !this.state.draggableProps) {
      return undefined
    }
    const {draggableProps} = this.state
    const {left, right, top, bottom} = this.state.ghost.getBoundingClientRect()
    const middleY = draggableProps.trigger === 'middleY'
    const allowOutside = draggableProps.allowPlaceholderOutsideMovement
    let lastDroppableInContainer: DroppableItem | undefined

    const droppable = this.state.droppables.find(droppable => {
      if (
        droppable.accepts &&
        !droppable.accepts.includes(draggableProps.data.type)
      ) {
        return false
      }

      if (!allowOutside && isOutsideContainer({droppable, e})) {
        return false
      }

      lastDroppableInContainer = droppable
      const rect = droppable.element.getBoundingClientRect()
      if (middleY) {
        const mid = (rect.top + rect.bottom) / 2
        return (
          (top > rect.top && top < mid) ||
          (bottom > mid && bottom < rect.bottom)
        )
      }
      const mid = (rect.left + rect.right) / 2
      return (
        (left > rect.left && left < mid) || (right > mid && right < rect.right)
      )
    })

    return lastDroppableInContainer
      ? {
          droppable,
          isAfter: false
        }
      : undefined
  }

  private processDragScroll(
    scrollContainers: ScrollContainer[],
    tPrev: DOMHighResTimeStamp | undefined,
    tCurr: DOMHighResTimeStamp
  ): void {
    const pMouse = this.state.mouseXY
    if (!this.state.dragStarted || !scrollContainers.length || !pMouse) {
      this.state.scrollInterval !== undefined &&
        cancelAnimationFrame(this.state.scrollInterval)
      return
    }

    const dt = tCurr - (tPrev ?? tCurr)
    for (const scrollContainer of scrollContainers) {
      this.processDragScrollForContainer(scrollContainer, pMouse, dt)
    }

    const cb = (tNext: DOMHighResTimeStamp): void =>
      this.processDragScroll(scrollContainers, tCurr, tNext)
    this.state.scrollInterval = requestAnimationFrame(cb)
  }

  private processDragScrollForContainer(
    scrollContainer: ScrollContainer,
    pMouse: XY,
    dt: DOMHighResTimeStamp
  ): void {
    const {container, direction, allowOutside} = scrollContainer
    const {thresholdX, thresholdY} = scrollContainer

    const rContainer = container.getBoundingClientRect()
    if (!insideRect(pMouse.x, pMouse.y, rContainer) && !allowOutside) {
      return
    }

    if (direction === 'x' || direction === 'both') {
      const speed = scrollSpeedXMax
      const dsMax = thresholdX

      if (container.scrollLeft < container.scrollWidth) {
        const ds = pMouse.x - rContainer.right + dsMax
        if (ds > 0) {
          const dx = this.getScrollDistancePerFrame({ds, dsMax, dt, speed})
          container.scrollLeft += dx
        }
      }

      if (container.scrollLeft > 0) {
        const ds = rContainer.left - pMouse.x + dsMax
        if (ds > 0) {
          const dx = this.getScrollDistancePerFrame({ds, dsMax, dt, speed})
          container.scrollLeft -= dx
        }
      }
    }

    if (direction === 'y' || direction === 'both') {
      const speed = scrollSpeedYMax
      const dsMax = thresholdY

      if (container.scrollTop < container.scrollHeight) {
        const ds = pMouse.y - rContainer.bottom + dsMax
        if (ds > 0) {
          const dy = this.getScrollDistancePerFrame({ds, dsMax, dt, speed})
          container.scrollTop += dy
        }
      }

      if (container.scrollTop > 0) {
        const ds = rContainer.top - pMouse.y + dsMax
        if (ds > 0) {
          const dy = this.getScrollDistancePerFrame({ds, dsMax, dt, speed})
          container.scrollTop -= dy
        }
      }
    }
  }

  private getScrollDistancePerFrame({
    ds,
    dsMax,
    dt,
    speed
  }: {
    ds: number
    dsMax: number
    dt: DOMHighResTimeStamp
    speed: number
  }): number {
    const max = dsMax * (1 - scrollThresholdMaxSpeedPart)
    const p = Math.max(0, Math.min(max, ds)) / max
    // The lower the `p` the greater the speed
    // `pow` is to give a function a slower start
    const mul = Math.pow(p * Math.exp(p), 2)
    return mul * speed * dt
  }

  onMouseDown(
    e: MouseEvent | React.MouseEvent,
    draggableProps: DraggableOnMouseDownProps
  ): void {
    if (
      !primaryButtonPressed(e) ||
      this.state.mouseDownXY ||
      UIUtil.isConsumed(e)
    ) {
      return
    }
    this.state.draggableProps = draggableProps
    this.state.mouseDownXY = {x: e.pageX, y: e.pageY}
    this.state.draggable = e.currentTarget as HTMLElement
    this.state.draggableRect = this.state.draggable.getBoundingClientRect()
    if (typeof draggableProps.ghost === 'function') {
      const ghost = cloneOriginElement(this.state.draggable)
      this.state.ghost = draggableProps.ghost(ghost)
    } else if (draggableProps.ghost) {
      this.state.ghost = draggableProps.ghost
    } else {
      this.state.ghost = cloneOriginElement(this.state.draggable)
    }
  }

  private dragStart(e: MouseEvent): void {
    if (this.state.dragStarted) {
      return
    }

    // Scroll container calculation must happen synchronously b/c at the next
    // animation frame draggable elem gets unmounted from the DOM and there's no
    // way to find its previous parents hierarchy
    const scrollContainers = this.prepareScrollContainers()

    requestAnimationFrame(() => {
      if (this.placeholderMode) {
        this.sortDroppablesIfRequired()

        const canBeHiddenOnDrag = !!this.state.draggableProps?.canBeHiddenOnDrag
        this.state.canBeHiddenOnDrag = canBeHiddenOnDrag
      }
      this.state.ghost && document.body.appendChild(this.state.ghost)
      this.updateGhostPosition(e)
      this.processDroppables(e)

      if (scrollContainers.length) {
        const cb = (tCurr: DOMHighResTimeStamp): void =>
          this.processDragScroll(scrollContainers, undefined, tCurr)
        this.state.scrollInterval = requestAnimationFrame(cb)
      }

      this.state.draggableProps?.clickablesOnHover &&
        setTimeout(() => DragClick.addDragClickables())
    })

    this.state.keydownHandler &&
      KeyboardHandlers.addKeydown(this.state.keydownHandler)

    this.state.dragStarted = true
    this.state.draggableProps?.onDragStart?.(
      this.state.draggableProps.data.payload
    )
  }

  private prepareScrollContainers(): ScrollContainer[] {
    const res: ScrollContainer[] = []

    for (const x of this.state.draggableProps?.scrollContainers ?? []) {
      const containers = x.absolute
        ? Array.from(document.querySelectorAll(x.selector))
        : [this.state.draggable?.closest(x.selector)]
      containers.forEach(c => {
        if (c) {
          const container = c as HTMLElement
          const rect = container.getBoundingClientRect()

          let thresholdX = defaultScrollThreshold
          let thresholdY = defaultScrollThreshold
          if (typeof x.thresholds !== 'undefined') {
            const thresholds =
              typeof x.thresholds === 'function'
                ? x.thresholds(rect.width, rect.height)
                : x.thresholds
            if (typeof thresholds === 'number') {
              thresholdX = thresholds
              thresholdY = thresholds
            } else {
              thresholdX = thresholds[0]
              thresholdY = thresholds[1]
            }
          }

          // To prevent cases where the sum of thresholds is larger than container dimensions
          const maxThresholdX = rect.width / 2
          const maxThresholdY = rect.height / 2
          thresholdX = Math.min(thresholdX, maxThresholdX)
          thresholdY = Math.min(thresholdY, maxThresholdY)

          res.push({
            container,
            direction: x.direction,
            allowOutside: x.allowOutside ?? false,
            thresholdX,
            thresholdY
          })
        }
      })
    }

    return res
  }

  private get placeholderMode(): boolean {
    return !!this.state.draggableProps?.placeholder
  }

  private sortDroppablesIfRequired(): void {
    if (!this.state.droppablesSortRequired || !this.state.draggableProps) {
      return
    }
    this.state.droppablesSortRequired = false
    const {trigger} = this.state.draggableProps
    this.state.droppables.sort((d1, d2) => {
      const rect1 = d1.element.getBoundingClientRect()
      const rect2 = d2.element.getBoundingClientRect()
      return trigger === 'middleY'
        ? rect1.top - rect2.top
        : rect1.left - rect2.left
    })
  }

  dragStop(e: MouseEvent): void {
    this.updateGhostPosition(e)
    this.processDroppables(e)

    const moved = this.moveDragToPlaceholder()

    if (moved) {
      droppablesDebounce.clear()
      this.state.mouseDownXY = undefined
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.executeEndEvents()
          this.dragStopCleanup()
        })
      })
    } else {
      this.executeEndEvents()
      this.dragStopCleanup()
    }
  }

  private moveDragToPlaceholder(): boolean {
    if (
      !this.state.ghost ||
      !this.state.placeholder ||
      !this.state.mouseDownXY ||
      !this.state.draggableRect
    ) {
      return false
    }

    if (this.state.draggableProps?.data.type === 'task') {
      const shouldSkip =
        this.state.draggableProps?.data.payload?.shouldCreateMirror ||
        this.state.canBeHiddenOnDrag
      if (shouldSkip) {
        return false
      }
    }

    const placeholderRect = this.state.placeholder.getBoundingClientRect()
    const dx = placeholderRect.left - this.state.draggableRect.left
    const dy = placeholderRect.top - this.state.draggableRect.top
    this.state.ghost.style.transform = `translate(${dx}px, ${dy}px)`
    return true
  }

  private dragStopCleanup(): void {
    droppablesDebounce.clear()
    this.state.scrollInterval !== undefined &&
      cancelAnimationFrame(this.state.scrollInterval)
    this.state.mouseDownXY = undefined

    requestAnimationFrame(() => {
      this.state.keydownHandler &&
        KeyboardHandlers.removeKeydown(this.state.keydownHandler)
      this.state.ghost && UtilUi.removeEl(this.state.ghost)
      this.state.placeholder && UtilUi.removeEl(this.state.placeholder)
      DragClick.removeDragClickables()

      runInAction(() => {
        this.state.draggable?.classList.remove('hidden')
        this.state.draggable = undefined
        this.state.currentDroppable = undefined
        this.state.placeholder = undefined
        this.state.isPlaceholderAfterDroppable = undefined
        // droppables order changes after dropping a draggable not in an original position
        this.state.droppablesSortRequired = true
        this.state.ghost = undefined
        this.state.canBeHiddenOnDrag = undefined
      })
    })

    this.state.dragStarted = false
  }

  private executeEndEvents(): void {
    if (!this.state.draggableProps) {
      return
    }

    let dropSuccessful = true

    try {
      this.state.currentDroppable?.onDrop?.(
        this.state.draggableProps.data,
        this.state.isPlaceholderAfterDroppable
      )
    } catch (error) {
      logger.error('Error in onDrop handler:', error)
      dropSuccessful = false
    }

    try {
      this.state.draggableProps.onDragEnd?.(
        this.state.draggableProps.data.payload
      )
    } catch (error) {
      logger.error('Error in onDragEnd handler:', error)
    }

    if (!dropSuccessful) {
      this.dragStopCleanup()
    }
  }

  onMouseUp(e: MouseEvent): void {
    if (!this.state.mouseDownXY) {
      return
    }

    e?.stopPropagation()
    e?.preventDefault()

    if (!this.state.draggableProps || !this.state.dragStarted) {
      this.state.mouseDownXY = undefined
      this.state.ghost = undefined
      return
    }

    this.dragStop(e)
  }

  private updatePlaceholder({droppable, isAfter}: PlaceholderPosition): void {
    // A placeholder should be removed both after dragging between droppables w/ placeholders
    // and from a droppable w/ a placeholder to a droppable w/o a placeholder
    this.state.placeholder && UtilUi.removeEl(this.state.placeholder)

    if (
      !droppable?.showPlaceholder ||
      !this.state.draggableProps ||
      !this.state.draggableRect
    ) {
      return
    }

    const placeholder =
      typeof this.state.draggableProps.placeholder === 'function'
        ? this.state.draggableProps.placeholder(this.state.draggableRect)
        : this.state.draggableProps.placeholder
    if (!placeholder) {
      return
    }
    const parent = droppable.element.parentElement
    const droppableEl = isAfter
      ? droppable.element.nextSibling
      : droppable.element
    parent?.insertBefore(placeholder, droppableEl)
    this.state.placeholder = placeholder
    this.state.isPlaceholderAfterDroppable = isAfter
  }
}

export const [DnDContext, useDnDStore] = createUseStore(DnDStore)

const DELTA_TO_DRAG_START = 6

const isInside = (rect: DragRectangle, e: MouseEvent): boolean => {
  return (
    e.clientY > rect.top &&
    e.clientY < window.innerHeight - rect.bottom &&
    e.clientX > rect.left &&
    e.clientX < window.innerWidth - rect.right
  )
}

interface XY {
  x: number
  y: number
}

const droppablesDebounce = new Debouncer(50)

type DraggableOnMouseDownProps = Pick<
  DraggableProps,
  | 'clickablesOnHover'
  | 'dropArea'
  | 'onDragStart'
  | 'onDragEnd'
  | 'ghost'
  | 'data'
  | 'placeholder'
  | 'scrollContainers'
  | 'trigger'
  | 'allowPlaceholderOutsideMovement'
  | 'canBeHiddenOnDrag'
>

export const defaultScrollThreshold = 55
// Last `scrollThresholdDelta` * 100% of a threshold give constant speed
const scrollThresholdMaxSpeedPart = 0.1
// Slower horizontal speed is more UX-friendly b/c it allows to see what content is scrolled over
const scrollSpeedXMax = 0.2
const scrollSpeedYMax = 0.8

interface PlaceholderPosition {
  droppable: DroppableItem | undefined
  isAfter: boolean
}
